import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Info, Loader2, ShieldCheck, Lock } from "lucide-react";
import api, { formatError } from "@/lib/api";
import Logo from "@/components/portal/Logo";
import AddressAutocomplete from "@/components/portal/AddressAutocomplete";
import OtpVerificationPanel from "@/components/portal/OtpVerificationPanel";
import { deriveInvoiceGroup } from "@/lib/invoice";
import { useOtpVerification } from "@/lib/otp";

// Lazy-load Razorpay Checkout so the SDK isn't pulled in for Group B customers.
const loadRazorpayCheckout = () =>
  new Promise((resolve, reject) => {
    if (window.Razorpay) return resolve(window.Razorpay);
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(window.Razorpay);
    script.onerror = () => reject(new Error("Failed to load Razorpay Checkout"));
    document.body.appendChild(script);
  });

const emptyAddr = {
  formatted_address: "",
  place_id: "",
  latitude: null,
  longitude: null,
  maps_url: "",
  street: "",
  city: "",
  state: "",
  pincode: "",
  country: "IN",
};

export default function DeliveryForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const invoice = location.state?.invoice_number || "";
  const groupType = deriveInvoiceGroup(invoice) || "";
  const [mapsKey, setMapsKey] = useState("");
  // Server-driven payment gate. Group A is always paid; Group B is paid
  // only when staff has flagged the specific invoice as `home_delivery_chargeable`.
  // We rely 100 % on this backend response — never on `groupType` — so
  // that the ₹300 badge, submit button copy, and Razorpay flow always
  // agree with the authoritative server value.
  const [paymentRequired, setPaymentRequired] = useState(false);
  const [amountPaise, setAmountPaise] = useState(30000);

  // Guardrail: invoice must come from the dedicated Invoice Number page.
  useEffect(() => {
    if (!groupType) navigate("/invoice", { replace: true });
  }, [groupType, navigate]);

  useEffect(() => {
    if (!groupType) return;
    let cancelled = false;
    api.get(`/invoice/${encodeURIComponent(invoice)}/payment-required`)
      .then(({ data }) => {
        if (cancelled) return;
        setPaymentRequired(!!data.payment_required);
        if (data.amount_paise) setAmountPaise(data.amount_paise);
      })
      .catch(() => { if (!cancelled) setPaymentRequired(false); });
    return () => { cancelled = true; };
  }, [groupType, invoice]);

  // Fields
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [address, setAddress] = useState(emptyAddr);
  const [flatWing, setFlatWing] = useState("");
  const [buildingSociety, setBuildingSociety] = useState("");
  const [landmark, setLandmark] = useState("");
  const [dupatta, setDupatta] = useState("");
  const [customisation, setCustomisation] = useState("no");
  const [customisationDetails, setCustomisationDetails] = useState("");
  const [notes, setNotes] = useState("");
  const [terms, setTerms] = useState(false);

  // Returning-customer address suggestion. Lookup is debounced so we
  // don't hammer the API while the customer is still typing digits.
  // `null` = no suggestion, `{...}` = one to offer. `dismissed` mobiles
  // are remembered so a customer who declined once isn't re-prompted
  // on every keystroke.
  const [recall, setRecall] = useState(null);
  const [recallDismissed, setRecallDismissed] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [otpContextNotice, setOtpContextNotice] = useState("");
  const otp = useOtpVerification();
  const { reset: resetOtp } = otp;
  const hadVerifiedOtp = useRef(false);
  const otpContextKey = JSON.stringify({
    invoice,
    groupType,
    fulfillmentType: "home_delivery",
    mobile,
  });

  const getOtpPayload = () => ({
    invoice_number: invoice,
    group_type: groupType,
    customer_name: name.trim(),
    mobile,
    address,
    flat_wing_floor: flatWing.trim(),
    building_society: buildingSociety.trim(),
    landmark: landmark.trim(),
    dupatta_status: dupatta,
    additional_customisation: customisation,
    customisation_details: customisation === "yes" ? customisationDetails.trim() : "",
    special_instructions: notes.trim(),
    terms_accepted: terms,
  });

  useEffect(() => {
    if (hadVerifiedOtp.current) {
      setOtpContextNotice("Mobile number, invoice, or delivery type changed — please verify again.");
    }
    resetOtp();
  }, [otpContextKey, resetOtp]);

  useEffect(() => {
    hadVerifiedOtp.current = otp.canProceed;
  }, [otp.canProceed]);
  const [touched, setTouched] = useState({});
  const touch = (k) => setTouched((t) => ({ ...t, [k]: true }));

  useEffect(() => {
    api.get("/config/maps").then((r) => setMapsKey(r.data.google_maps_api_key || "")).catch(() => {});
  }, []);

  const mobileValid = /^[6-9]\d{9}$/.test(mobile);
  const addressValid = Boolean(address.formatted_address || address.street);

  // Debounced recall lookup — fires only when mobile is complete + valid.
  // We deliberately do NOT auto-apply the address; the customer taps
  // "Use this address" to accept, so an accidental typo of someone
  // else's number never silently overwrites their form.
  useEffect(() => {
    if (!mobileValid) { setRecall(null); return; }
    if (recallDismissed) return;
    // Skip if the customer has already filled the address themselves.
    if (address.formatted_address || address.street) return;
    let cancelled = false;
    const t = setTimeout(() => {
      api.post("/customer/last-address", { mobile, invoice_number: invoice })
        .then(({ data }) => { if (!cancelled && data?.found) setRecall(data); })
        .catch(() => { /* silent — recall is a bonus, never a blocker */ });
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mobile, mobileValid, recallDismissed]);

  const applyRecall = () => {
    if (!recall) return;
    if (!name.trim() && recall.customer_name) setName(recall.customer_name);
    setAddress({ ...emptyAddr, ...(recall.address || {}) });
    if (recall.flat_wing_floor) setFlatWing(recall.flat_wing_floor);
    if (recall.building_society) setBuildingSociety(recall.building_society);
    if (recall.landmark) setLandmark(recall.landmark);
    toast.success("Address auto-filled from your last order");
    setRecall(null);
  };
  const dismissRecall = () => { setRecall(null); setRecallDismissed(true); };

  const errors = {
    name: !name.trim() ? "Please enter your name as printed on the invoice." : null,
    mobile: !mobileValid ? "Please enter a valid 10-digit Indian mobile number." : null,
    address: !addressValid ? "Please select or enter your delivery address." : null,
    flatWing: !flatWing.trim() ? "Please enter your flat, wing or floor details." : null,
    dupatta: !dupatta ? "Please select your dupatta status." : null,
    customisation:
      !customisation
        ? "Please make a selection."
        : customisation === "yes" && !customisationDetails.trim()
        ? "Please describe your additional paid customisation(s)."
        : null,
    terms: !terms ? "Please accept the terms to continue." : null,
  };
  const err = (k) => (touched[k] ? errors[k] : null);

  const canSubmit = !Object.values(errors).some(Boolean) && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    if (otp.required && !otp.canProceed) {
      toast.error("Please verify your mobile number before continuing.");
      return;
    }
    setSubmitting(true);
    const payload = getOtpPayload();
    try {
      // Server-decided payment gate — Group A always, Group B only when
      // staff flagged the invoice as `home_delivery_chargeable`.
      if (paymentRequired) {
        const { data: order } = await api.post("/payments/create-order", payload);
        const Razorpay = await loadRazorpayCheckout();
        const rzp = new Razorpay({
          key: order.key_id,
          amount: order.amount,
          currency: order.currency,
          order_id: order.order_id,
          name: "Tanish Textiles",
          description: `Home Delivery · Invoice ${invoice}`,
          prefill: { name: payload.customer_name, contact: payload.mobile },
          theme: { color: "#6A1F2B" },
          modal: {
            // Customer closed the modal — keep the form data so they can retry.
            ondismiss: () => {
              setSubmitting(false);
              toast.info("Payment was not completed. Your details are saved — please try again.");
            },
          },
          handler: async (resp) => {
            try {
              const { data } = await api.post("/payments/verify", {
                razorpay_order_id: resp.razorpay_order_id,
                razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_signature: resp.razorpay_signature,
              });
              navigate("/success", {
                state: {
                  submission_id: data.submission_id,
                  invoice_number: data.invoice_number,
                  submitted_at: data.submitted_at,
                  whatsapp_scheduled: data.whatsapp_scheduled,
                  group_type: data.group_type,
                  paid: true,
                },
                replace: true,
              });
            } catch (e) {
              // Signature verification failed on the backend — do NOT create
              // a submission, do NOT navigate to success. Keep form data.
              toast.error(formatError(e) || "We couldn't verify your payment. Your details are saved — please try again.");
              setSubmitting(false);
            }
          },
        });
        rzp.on("payment.failed", (resp) => {
          toast.error(
            resp?.error?.description
              || "Payment was not completed. Your details are saved — please try again."
          );
          setSubmitting(false);
        });
        rzp.open();
      } else {
        const { data } = await api.post("/submissions", payload);
        navigate("/success", {
          state: {
            submission_id: data.submission_id,
            invoice_number: data.invoice_number,
            submitted_at: data.submitted_at,
            whatsapp_scheduled: data.whatsapp_scheduled,
            group_type: data.group_type,
          },
          replace: true,
        });
      }
    } catch (e) {
      toast.error(formatError(e));
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-ivory">
      <header className="px-6 sm:px-10 pt-6">
        <button
          data-testid="back-home-btn"
          onClick={() => navigate("/")}
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-muted2 hover:text-maroon transition-colors"
        >
          <ArrowLeft size={14} strokeWidth={1.6} />
          Back
        </button>
        <div className="flex justify-center mt-5">
          <Logo size="sm" withMonogram={false} />
        </div>
      </header>

      <main className="px-4 sm:px-6 py-10 sm:py-14">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-xl mx-auto"
        >
          <div className="text-center mb-10">
            <h2 className="font-serif font-light text-4xl sm:text-5xl text-ink">
              Delivery Details
            </h2>
            <div className="divider-thin" />
            <p className="text-sm text-muted2 max-w-md mx-auto leading-relaxed">
              Please keep your billing invoice ready. The information below will only
              be used to prepare and deliver your order safely.
            </p>
            {paymentRequired && (
              <div
                data-testid="paid-delivery-notice"
                className="mt-5 inline-flex items-center gap-2 rounded-full bg-maroon/[0.06] border border-maroon/25 text-maroon text-xs font-medium px-3.5 py-1.5"
              >
                <Lock size={12} strokeWidth={1.9} />
                Home Delivery Charge · ₹{Math.round((amountPaise || 30000) / 100)} · paid securely at checkout
              </div>
            )}
          </div>

          <div className="bg-white rounded-3xl border border-line/60 shadow-[0_8px_40px_rgba(44,26,29,0.04)] p-6 sm:p-10">
            {/* Invoice summary — captured on the previous page, shown read-only for reassurance */}
            <div
              data-testid="invoice-summary"
              className="mb-6 flex items-center justify-between rounded-2xl border border-line bg-ivory/50 px-4 py-3"
            >
              <div>
                <p className="overline text-[10px]">Invoice Number</p>
                <p className="text-sm font-medium text-ink mt-0.5">{invoice}</p>
              </div>
              <button
                type="button"
                data-testid="change-invoice-btn"
                onClick={() => navigate("/invoice")}
                className="text-[11px] text-maroon hover:underline"
              >
                Change
              </button>
            </div>

            {/* Name */}
            <Field label="Customer Name" required>
              <input
                data-testid="name-input"
                className="luxury-input"
                placeholder="As mentioned on the invoice"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={() => touch("name")}
              />
              {err("name") && (
                <p data-testid="name-error" className="mt-2 text-[11px] text-[#8A2A2A]">{err("name")}</p>
              )}
            </Field>

            {/* Mobile */}
            <Field label="Mobile Number" required hint="10-digit Indian mobile number">
              <div className="flex gap-2">
                <div className="luxury-input !w-auto !py-3 flex items-center text-muted2 text-sm">+91</div>
                <input
                  data-testid="mobile-input"
                  className="luxury-input flex-1"
                  inputMode="numeric"
                  maxLength={10}
                  placeholder="10-digit number"
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
                  onBlur={() => touch("mobile")}
                />
              </div>
              {(err("mobile") || (mobile.length === 10 && !mobileValid)) && (
                <p data-testid="mobile-error" className="mt-2 text-[11px] text-[#8A2A2A]">
                  {mobile.length === 10 && !mobileValid
                    ? "Please enter a valid Indian mobile number starting with 6, 7, 8, or 9."
                    : "Please enter a valid 10-digit Indian mobile number."}
                </p>
              )}
              {recall && (
                <div
                  data-testid="recall-suggestion"
                  className="mt-3 rounded-2xl border border-maroon/25 bg-maroon/[0.04] p-3.5"
                >
                  <div className="flex items-start gap-2.5">
                    <div className="h-8 w-8 rounded-full bg-maroon/[0.10] text-maroon flex items-center justify-center flex-shrink-0">
                      <ShieldCheck size={14} strokeWidth={1.8} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[11px] uppercase tracking-[0.22em] text-maroon font-medium">Welcome back</p>
                      <p className="text-sm text-ink mt-1 leading-relaxed">
                        We found your last delivery address
                        {recall.customer_name ? <> for <span className="font-medium">{recall.customer_name}</span></> : null}.
                      </p>
                      <p className="text-[11px] text-muted2 mt-1 leading-relaxed break-words">
                        {recall.flat_wing_floor ? `${recall.flat_wing_floor}, ` : ""}
                        {recall.address?.formatted_address || ""}
                      </p>
                      <div className="mt-3 flex flex-col-reverse sm:flex-row sm:items-center gap-2">
                        <button
                          type="button"
                          data-testid="recall-dismiss"
                          onClick={dismissRecall}
                          className="text-[11px] uppercase tracking-[0.22em] text-muted2 hover:text-maroon transition-colors sm:mr-2"
                        >
                          No, enter a new one
                        </button>
                        <button
                          type="button"
                          data-testid="recall-apply"
                          onClick={applyRecall}
                          className="btn-maroon !px-4 !py-2 !text-xs inline-flex items-center justify-center gap-1.5"
                        >
                          <ArrowRight size={13} strokeWidth={1.7} /> Use this address
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <OtpVerificationPanel
                otp={otp}
                mobileValid={mobileValid}
                readyForVerification={mobileValid}
                getPayload={getOtpPayload}
                contextKey={otpContextKey}
                contextNotice={otpContextNotice}
                onRequestStarted={() => setOtpContextNotice("")}
              />
            </Field>

            {/* Flat / House No. — customer manually types their unit info.
                Kept SEPARATE from the Google Places field so customers can't
                accidentally lose their flat number inside a formatted address. */}
            <Field label="Flat / House No." required>
              <input
                data-testid="flat-input"
                className="luxury-input"
                placeholder="e.g. B-1204, Flat 12, House No. 18"
                value={flatWing}
                onChange={(e) => setFlatWing(e.target.value)}
                onBlur={() => touch("flatWing")}
              />
              <p className="mt-1.5 text-[11px] text-muted2 leading-relaxed">
                Enter your flat, house, wing or unit number.
              </p>
              {err("flatWing") && (
                <p data-testid="flat-error" className="mt-1.5 text-[11px] text-[#8A2A2A]">{err("flatWing")}</p>
              )}
            </Field>

            {/* Building / Society Name — optional manual entry, never
                overwritten by the Google selection. */}
            <Field label="Building / Society Name">
              <input
                data-testid="building-input"
                className="luxury-input"
                placeholder="e.g. Sea Breeze Apartments"
                value={buildingSociety}
                onChange={(e) => setBuildingSociety(e.target.value)}
              />
              <p className="mt-1.5 text-[11px] text-muted2 leading-relaxed">
                Optional — your apartment complex, society or tower name.
              </p>
            </Field>

            {/* Google-powered Delivery Location — visually distinct from the
                two manual fields above so the customer understands this is
                a search-and-select experience, not free text. Uses the
                same `mt-5` gap as the sibling <Field> blocks for a
                consistent form rhythm. */}
            <div className="mt-5">
              <div className="flex items-baseline justify-between gap-2 flex-wrap mb-2">
                <label className="block text-[11px] uppercase tracking-[0.24em] text-ink">
                  Delivery Location <span className="text-maroon">*</span>
                </label>
                <span className="text-[10px] uppercase tracking-[0.18em] text-muted2 inline-flex items-center gap-1 flex-shrink-0">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <path d="M12 2a8 8 0 0 1 8 8c0 5.5-8 12-8 12s-8-6.5-8-12a8 8 0 0 1 8-8z"/>
                    <circle cx="12" cy="10" r="3"/>
                  </svg>
                  Google Maps
                </span>
              </div>

              <div
                data-testid="delivery-location-picker"
                className="rounded-2xl border-2 border-maroon/25 bg-gradient-to-br from-white to-ivory/40 p-3.5 sm:p-4"
                onBlur={() => touch("address")}
              >
                <AddressAutocomplete
                  apiKey={mapsKey}
                  value={address}
                  onChange={(v) => { setAddress(v); touch("address"); }}
                />
              </div>
              {err("address") && (
                <p data-testid="address-error" className="mt-2 text-[11px] text-[#8A2A2A]">{err("address")}</p>
              )}
            </div>

            {/* Landmark — optional manual entry, never routed through Google. */}
            <Field label="Landmark">
              <input
                data-testid="landmark-input"
                className="luxury-input"
                placeholder="e.g. Opp. City Mall / Near main gate"
                value={landmark}
                onChange={(e) => setLandmark(e.target.value)}
              />
              <p className="mt-1.5 text-[11px] text-muted2 leading-relaxed">
                Optional — helps our delivery partner locate you.
              </p>
            </Field>

            {/* Dupatta */}
            <Field label="Dupatta Status" required>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  { v: "collected", t: "I have already collected my dupatta." },
                  { v: "include", t: "My dupatta is still with Tanish Textiles. Please include it in this shipment." },
                ].map(({ v, t }) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => { setDupatta(v); touch("dupatta"); }}
                    data-testid={`dupatta-${v}`}
                    className={`text-left rounded-2xl border p-4 transition-all ${
                      dupatta === v
                        ? "border-maroon bg-maroon/[0.04] shadow-[0_4px_20px_-8px_rgba(106,31,43,0.35)]"
                        : "border-line hover:border-maroon/40"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <span
                        className={`mt-1 h-4 w-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                          dupatta === v ? "border-maroon" : "border-line"
                        }`}
                      >
                        {dupatta === v && <span className="h-2 w-2 rounded-full bg-maroon" />}
                      </span>
                      <span className="text-sm text-ink leading-relaxed">{t}</span>
                    </div>
                  </button>
                ))}
              </div>
              {err("dupatta") && (
                <p data-testid="dupatta-error" className="mt-2 text-[11px] text-[#8A2A2A]">{err("dupatta")}</p>
              )}
            </Field>

            {/* Additional Paid Customisations */}
            <Field label="Additional Paid Customisations" required>
              <p className="text-sm text-ink leading-relaxed mb-2">
                Do you have any additional paid customisations to be included with your delivery?
              </p>
              <p className="text-[11px] text-muted2 leading-relaxed mb-3">
                This does not include your regular lehenga or blouse stitching. Please select “Yes” only if you requested and paid for additional customisations beyond our standard stitching service.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  { v: "no", t: "No, I don’t have any additional paid customisations." },
                  { v: "yes", t: "Yes, please include my additional paid customisations." },
                ].map(({ v, t }) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => {
                      setCustomisation(v);
                      if (v === "no") setCustomisationDetails("");
                    }}
                    data-testid={`customisation-${v}`}
                    className={`text-left rounded-2xl border p-4 transition-all ${
                      customisation === v
                        ? "border-maroon bg-maroon/[0.04] shadow-[0_4px_20px_-8px_rgba(106,31,43,0.35)]"
                        : "border-line hover:border-maroon/40"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <span
                        className={`mt-1 h-4 w-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                          customisation === v ? "border-maroon" : "border-line"
                        }`}
                      >
                        {customisation === v && <span className="h-2 w-2 rounded-full bg-maroon" />}
                      </span>
                      <span className="text-sm text-ink leading-relaxed">{t}</span>
                    </div>
                  </button>
                ))}
              </div>
              <AnimatePresence initial={false}>
                {customisation === "yes" && (
                  <motion.div
                    key="customisation-details"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                    className="overflow-hidden"
                  >
                    <div className="mt-5">
                      <label className="block text-[11px] uppercase tracking-[0.24em] text-ink mb-2">
                        Please describe your additional paid customisation(s).
                      </label>
                      <textarea
                        data-testid="customisation-details-input"
                        rows={6}
                        className="luxury-input luxury-textarea !min-h-[180px] sm:!min-h-[160px]"
                        placeholder="Example: Customised dupatta, additional embroidery, matching potli bag, extra sleeves, or any other paid customisation requested beyond the standard stitching service."
                        value={customisationDetails}
                        onChange={(e) => setCustomisationDetails(e.target.value)}
                        onBlur={() => touch("customisation")}
                      />
                      {err("customisation") && customisation === "yes" && (
                        <p data-testid="customisation-details-error" className="mt-2 text-[11px] text-[#8A2A2A]">{err("customisation")}</p>
                      )}
                      <p className="text-[11px] text-muted2 italic mt-2 leading-relaxed">
                        Our team will verify these customisations against your original order before dispatch.
                      </p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </Field>

            {/* Notes */}
            <Field label="Special Instructions">
              <textarea
                data-testid="notes-input"
                className="luxury-input luxury-textarea"
                placeholder="Preferred timing, security gate code, building instructions, call before delivery, etc."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </Field>

            {/* Informational note */}
            <div
              data-testid="finishing-note"
              className="mt-6 flex items-start gap-3 rounded-2xl border border-line bg-ivory/60 px-4 py-4 sm:px-5"
              aria-live="polite"
            >
              <Info className="text-maroon mt-0.5 flex-shrink-0" size={16} strokeWidth={1.6} />
              <div>
                <p className="text-[11px] uppercase tracking-[0.22em] text-maroon font-medium">
                  Please Note
                </p>
                <p className="text-sm text-ink leading-relaxed mt-1.5">
                  After the scheduled stitching date, kindly allow 2–3 days for the final finishing of your outfit.
                </p>
              </div>
            </div>

            {/* Terms */}
            <label className="mt-4 flex items-start gap-3 cursor-pointer" data-testid="terms-label">
              <input
                type="checkbox"
                data-testid="terms-checkbox"
                checked={terms}
                onChange={(e) => { setTerms(e.target.checked); touch("terms"); }}
                className="mt-1 h-4 w-4 accent-maroon"
              />
              <span className="text-xs text-muted2 leading-relaxed">
                I confirm that all information provided above is accurate and will be
                used by Tanish Textiles only for preparing and delivering my order.
              </span>
            </label>
            {err("terms") && (
              <p data-testid="terms-error" className="mt-2 ml-7 text-[11px] text-[#8A2A2A]">{err("terms")}</p>
            )}

            <button
              data-testid="submit-delivery-btn"
              onClick={submit}
              disabled={!canSubmit || otp.loading || (otp.required && !otp.canProceed)}
              className="btn-maroon w-full mt-8 text-base disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <Loader2 className="animate-spin" size={18} />
                  {paymentRequired ? "Opening secure checkout…" : "Submitting…"}
                </>
              ) : otp.required && !otp.canProceed ? (
                <>
                  <ShieldCheck size={16} strokeWidth={1.7} />
                  Verify mobile to continue
                </>
              ) : paymentRequired ? (
                <>
                  <Lock size={16} strokeWidth={1.7} />
                  Pay ₹{Math.round((amountPaise || 30000) / 100)} &amp; Confirm Delivery
                  <ArrowRight size={18} strokeWidth={1.6} />
                </>
              ) : (
                <>
                  Submit Delivery Details
                  <ArrowRight size={18} strokeWidth={1.6} />
                </>
              )}
            </button>

            <p className="mt-5 flex items-center justify-center gap-2 text-[11px] uppercase tracking-[0.24em] text-muted2">
              <ShieldCheck size={13} strokeWidth={1.5} />
              {paymentRequired
                ? "Secure payment · Razorpay"
                : "Your details are handled with utmost care"}
            </p>
          </div>
        </motion.div>
      </main>


    </div>
  );
}

const Field = ({ label, required, hint, children }) => (
  <div className="mt-5 first:mt-0">
    <label className="block text-[11px] uppercase tracking-[0.24em] text-ink mb-2">
      {label} {required && <span className="text-maroon">*</span>}
    </label>
    {children}
    {hint && <p className="text-[11px] text-muted2 mt-1.5">{hint}</p>}
  </div>
);
